import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/** How a report appears in the email body. */
export type ReportScheduleEmailModeModel = 'highlights' | 'table' | 'attachment';

/**
 * PD-17398 — one report in a scheduled email, in order: its report id, its
 * own filters (dates always relative, e.g. `periodType: lastWeek`), an
 * optional title with placeholders from its filters, and how it appears in
 * the email.
 */
@Index('idx_report_schedule_item_schedule', ['fkReportScheduleId', 'sortOrder'])
@Entity('report_schedule_item')
export class ReportScheduleItemEntity extends BaseEntity {
	@Column('int', { name: 'fk_report_schedule__id', unsigned: true })
	public fkReportScheduleId!: number;

	@Column('int', { name: 'sortOrder', default: 0 })
	public sortOrder!: number;

	/** The report id the data endpoint dispatches on, e.g. `user-activity`. */
	@Column('varchar', { name: 'reportKey', length: 120 })
	public reportKey!: string;

	/**
	 * The report's name when it was added (the screen's title). A company's
	 * own name for the report (report_label) still wins when it sends.
	 */
	@Column('varchar', { name: 'reportName', length: 200, nullable: true })
	public reportName!: string | null;

	/** Uses its own date range (in `filters`) instead of the schedule's. */
	@Column({ type: 'boolean', name: 'ownRange', width: 1, default: 0 })
	public ownRange!: boolean;

	/** The report's filters as its screen sends them. */
	@Column('json', { name: 'filters', nullable: true })
	public filters!: Record<string, unknown> | null;

	/** The filters in words, as chosen ("Team: Ohio"), for the report's header. */
	@Column('json', { name: 'filterLabels', nullable: true })
	public filterLabels!: Array<string> | null;

	/** Title, e.g. `{report}: {team}`. Null uses the report's (custom) name. */
	@Column('varchar', { name: 'titleTemplate', length: 300, nullable: true })
	public titleTemplate!: string | null;

	@Column('varchar', { name: 'emailMode', length: 20, default: 'highlights' })
	public emailMode!: ReportScheduleEmailModeModel;

	/** The report screen's path, for "Open in Workd". */
	@Column('varchar', { name: 'openPath', length: 255, nullable: true })
	public openPath!: string | null;
}
